import React from 'react';
import round from 'lodash/round';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import BarYAxisLabel from './BarYAxisLabel';
import GeneralChart from '@/components/job-title/GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import { DataType } from './index';
import { JobTitleContext } from '@/context/create-context';
import { SCALE_BAR_TIP } from '@/constants/g2plot';
import {
  EDUCATION_BENEFITS_SEO_MAP,
  EDUCATION_MAP,
} from '@/constants/job-title';
import { max, numberRound } from '@/utils/numbers';

const SalaryEducationCard = () => {
  const {
    jobTitle: {
      salary_details: {
        avg_value_by_education_details: avgValueByEducationDetails,
      },
      city_name: cityName = '',
    },
    displayName,
  } = React.useContext(JobTitleContext);
  const barChartRef = React.useRef(null);

  const [educationChartData, educationBarTipPercentage] = React.useMemo<
    [DataType, Record<string, number>]
  >(() => {
    const data: DataType = [];
    const educationBarTipPercentage: { [key: string]: number } = {};
    const educationMax = max(
      Object.values(avgValueByEducationDetails).map((item: any) => item['max']),
    );
    Object.keys(EDUCATION_MAP).forEach((key) => {
      const salaryRange = Reflect.get(avgValueByEducationDetails, key);
      if (!salaryRange) return;
      data.push({
        label: EDUCATION_MAP[key],
        range: [salaryRange.min, salaryRange.max],
      });
      const percentage =
        (SCALE_BAR_TIP * educationMax) / (salaryRange.max - salaryRange.min);
      educationBarTipPercentage[EDUCATION_MAP[key]] =
        percentage >= 1 ? 1 : percentage <= 0 ? 0 : percentage;
    });
    return [data, educationBarTipPercentage];
  }, [avgValueByEducationDetails]);

  const seoValue = React.useMemo(() => {
    const data: string[] = [];
    Object.keys(EDUCATION_MAP).forEach((key) => {
      const salaryRange = Reflect.get(avgValueByEducationDetails, key);
      if (!salaryRange) return;
      data.push(
        `${EDUCATION_BENEFITS_SEO_MAP[key]}薪资区间为${round(
          salaryRange.min,
          -3,
        )}元至${round(salaryRange.max, -3)}元`,
      );
    });
    return data;
  }, [avgValueByEducationDetails]);

  const barConfig = React.useMemo<ECOption>(() => {
    const data = [...educationChartData];
    const reverseData = data.reverse();
    const chartLowData = reverseData.map(({ label, range }) => ({
      name: label,
      value: range[0],
    }));
    const chartHighData = reverseData.map(({ label, range }) => ({
      name: label,
      value: range[1] - range[0],
    }));

    return {
      grid: {
        show: false,
        containLabel: true,
        top: 0,
        bottom: 0,
        left: 25,
        right: 30,
      },
      xAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: 'rgba(0,0,0,0.85)',
          formatter: (item: number) => numberRound(item),
          margin: 15,
        },
        splitLine: { show: false },
        splitNumber: 4,
      },
      yAxis: {
        interval: true,
        type: 'category',
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: true, lineStyle: { width: 1, color: '#f0f0f0' } },
      },
      tooltip: {
        confine: true,
        padding: 10,
        borderColor: '#f0f0f0',
        borderWidth: 1,
        backgroundColor: '#fff',
        shadowColor: 'transparent',
        textStyle: { fontSize: 12, fontWeight: 500 },
        formatter: (params) => {
          const name = 'name' in params ? params.name : '';
          const lowValue =
            chartLowData.find((item) => item.name === name)?.value || 0;
          const highValue =
            chartHighData.find((item) => item.name === name)?.value || 0;
          return `<p>${name}</p><p style="margin-top: 4px;">平均薪资: <span style="color:#00575c">¥${numberRound(
            (lowValue * 2 + highValue) / 2,
          )}</span></p>`;
        },
      },
      series: [
        {
          type: 'bar',
          data: chartLowData,
          emphasis: { disabled: true },
          cursor: 'default',
          barWidth: 30,
          itemStyle: {
            borderWidth: 0,
            color: '#fff',
          },
          stack: 'total',
        },
        {
          type: 'bar',
          data: chartHighData,
          barWidth: 30,
          emphasis: { disabled: true },
          cursor: 'default',
          stack: 'total',
          label: {
            show: true,
            position: 'insideLeft',
            color: 'rgba(0, 0, 0, 0.85)',
            formatter: ({ dataIndex, value }) => {
              const lowData = chartLowData[dataIndex].value;
              return `${numberRound(lowData)}-${numberRound(
                lowData + Number(value),
              )}`;
            },
          },
          itemStyle: {
            borderWidth: 0,
            borderColor: 'transparent',
            shadowColor: 'transparent',
            borderRadius: 1,
            color: ({ name }) => {
              const percentage = educationBarTipPercentage[name];
              return {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 1,
                y2: 0,
                colorStops: [
                  {
                    offset: 0,
                    color: '#08a17b',
                  },
                  {
                    offset: percentage,
                    color: '#08a17b',
                  },
                  {
                    offset: percentage,
                    color: 'rgba(8, 161, 123, 0.09)',
                  },
                  {
                    offset: 1,
                    color: 'rgba(8, 161, 123, 0.09)',
                  },
                ],
              };
            },
          },
        },
      ],
    };
  }, [educationChartData, educationBarTipPercentage]);

  return (
    <GeneralChart
      type="custom"
      title={`${cityName}不同学历的${displayName}收入相差多少?`}
      subTitle={
        <>
          <SchoolOutlinedIcon className="inline-block mr-1" />
          学历对应的薪资区间
        </>
      }
      description={
        <DataInterpretationSEO
          lineClamp="line-clamp-none"
          className="font-normal text-opacity-85"
        >
          {`学历对于${cityName}${displayName}的收入有多大的影响？${seoValue.join(
            '，',
          )}。本统计数据依据于各平台公开薪酬数据所得，时间不同可能会产生波动，仅供参考。`}
        </DataInterpretationSEO>
      }
    >
      <div className="px-5 pb-6 flex tablet:px-12">
        <BarYAxisLabel
          chartData={educationChartData}
          jobTitleName={`${cityName}${displayName}`}
        />
        <div className="w-full grow [&_canvas]:cursor-default">
          <CusEcharts chartRef={barChartRef} config={barConfig} />
        </div>
      </div>
    </GeneralChart>
  );
};

export default SalaryEducationCard;
